import { forwardRef, ReactNode, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, Flex, FormControl, FormLabel, HStack, Heading, Icon, Input, InputGroup, InputLeftElement, Menu, MenuButton, MenuItem, MenuList, Radio, RadioGroup, Select, SimpleGrid, Spinner, Switch, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, VStack } from "@chakra-ui/react";
import { showDateTime } from "../../utilities/helper";
import { CustomToast } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import { inr } from "./customerDetail";
import { cardProps, ICONS, labelProps, StatCard } from "./billingui";
import InvoicePdfButtons from "./Invoicepdfbuttons";
// import UniversalActivityHistory from "../../components/History";
import { ServiceType } from "../Project/model";
import BillingPlanList from "./billingplanlist";
import { CURRENCIES, formatMoney } from "../../utilities/money";

/* ───────────────────────── types & constants ───────────────────────── */

interface Form {
  billingType: number; // 1 = One time, 2 = Recurring
  billingFrequency: number;
  startDate: string; // invoice date (one-time) / start date (recurring)
  nextInvoiceDate: string;
  dueDate: string;
  endDate?: string | null; // kept from saved config, not shown in the UI
  paymentTermsDays: number;
  gstPercent: number;
  autoGenerateInvoice: boolean; // "Generate Invoice Now" (one-time) / "Auto-generate invoice" (recurring)
  autoEmailClient: boolean;
  currencyCode: string;
}

interface Row {
  key: string;
  serviceInterestedInId?: number;
  custom?: boolean;
  baseAmount?: number; // monthly project amount, used to rescale when the cycle changes
  name: string;
  description: string;
  quantity: string;
  unitPrice: string;
  gstPercent: number;
}

interface Opt {
  value: number;
  label: string;
  sub?: string;
  tag: string;
}

// NOTE: 2,3,4,5,6 are the values you already had. 7,8,9 are NEW – add them to the backend enum.
const CYCLES: (Opt & { months: number })[] = [
  { value: 2, label: "Weekly", sub: "Every week", tag: "7", months: 12 / 52 },
  { value: 7, label: "Bi-Weekly", sub: "Every 2 weeks", tag: "14", months: 12 / 26 },
  { value: 8, label: "Four Weekly", sub: "Every 4 weeks", tag: "28", months: 12 / 13 },
  { value: 9, label: "Semi-Monthly", sub: "Twice in a month (1st & 16th)", tag: "½", months: 0.5 },
  { value: 3, label: "Monthly", sub: "Every month", tag: "1M", months: 1 },
  { value: 4, label: "Quarterly", sub: "Every 3 months", tag: "3M", months: 3 },
  { value: 5, label: "Half-Yearly", sub: "Every 6 months", tag: "6M", months: 6 },
  { value: 6, label: "Yearly", sub: "Every 12 months", tag: "12M", months: 12 },
];
const monthsFor = (f: number) => CYCLES.find((c) => c.value === f)?.months ?? 1;
const amountOf = (b: any) => Number(b?.projectAmount ?? b?.amount ?? b?.budgetAmount ?? b?.monthlyBudget ?? b?.serviceAmount ?? 0) || 0;
const TERMS: Opt[] = [7, 15, 30, 45, 60, 90].map((n) => ({ value: n, label: `${n} Days`, tag: String(n) }));
const GST_OPTIONS = [0, 5, 12, 18, 28];

const inputProps = { size: "sm", borderRadius: "md" } as const;
const NAVY = "#0F2A5C";

/* ───────────────────────── helpers ───────────────────────── */

const p2 = (x: number) => String(x).padStart(2, "0");
const toYmd = (dt: Date) => `${dt.getFullYear()}-${p2(dt.getMonth() + 1)}-${p2(dt.getDate())}`;
const today = () => toYmd(new Date());
const d = (v?: string | null) => (v ? v.substring(0, 10) : "");
const addDays = (s: string, n: number) => {
  if (!s) return "";
  const dt = new Date(`${s}T00:00:00`);
  dt.setDate(dt.getDate() + n);
  return toYmd(dt);
};
const r2 = (n: number) => Math.round(n * 100) / 100;
const lineBase = (r: Row) => (Number(r.quantity) || 0) * (Number(r.unitPrice) || 0);
const lineTotal = (r: Row) => lineBase(r) * (1 + (Number(r.gstPercent) || 0) / 100);

const makeDefaults = (cur = "INR"): Form => ({
  billingType: 2,
  billingFrequency: 3,
  startDate: today(),
  nextInvoiceDate: today(),
  dueDate: addDays(today(), 30),
  endDate: null,
  paymentTermsDays: 30,
  gstPercent: 18,
  autoGenerateInvoice: true,
  autoEmailClient: true,
  currencyCode: cur,
});

/* ───────────────────────── small UI pieces ───────────────────────── */

const Ico = ({ children, ...p }: any) => (
  <Icon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" boxSize={4} {...p}>
    {children}
  </Icon>
);

const I = {
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  doc: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </>
  ),
  layers: (
    <>
      <path d="M12 2 2 7l10 5 10-5-10-5z" />
      <path d="m2 17 10 5 10-5M2 12l10 5 10-5" />
    </>
  ),
  percent: (
    <>
      <path d="M19 5 5 19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </>
  ),
  calc: (
    <>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 6h8M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
    </>
  ),
  sliders: <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />,
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  trash: <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" />,
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  chevron: <path d="m6 9 6 6 6-6" />,
  card: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </>
  ),
};

const Req = (
  <Text as="span" color="red.500">
    {" "}
    *
  </Text>
);

const SectionTitle = ({ icon, title, sub }: { icon: ReactNode; title: string; sub?: string }) => (
  <Flex align="center" gap={3}>
    <Flex w="34px" h="34px" flexShrink={0} bg="blue.50" color="blue.600" rounded="lg" align="center" justify="center">
      <Ico boxSize={5}>{icon}</Ico>
    </Flex>
    <Box>
      <Text fontWeight="bold" color={NAVY} fontSize="md" lineHeight="short">
        {title}
      </Text>
      {sub && (
        <Text fontSize="xs" color="gray.500">
          {sub}
        </Text>
      )}
    </Box>
  </Flex>
);

const IconBox = ({ children }: { children: ReactNode }) => (
  <Flex w="30px" h="30px" flexShrink={0} bg="blue.50" color="blue.600" rounded="md" align="center" justify="center">
    <Ico>{children}</Ico>
  </Flex>
);

const TagBox = ({ children }: { children: ReactNode }) => (
  <Flex w="26px" h="26px" flexShrink={0} border="1px solid" borderColor="blue.200" color="blue.600" bg="white" rounded="md" align="center" justify="center" fontSize="9px" fontWeight="bold">
    {children}
  </Flex>
);

const DateInput = forwardRef<HTMLInputElement, any>((props, ref) => (
  <InputGroup size="sm">
    <InputLeftElement pointerEvents="none" color="gray.500">
      <Ico>{I.calendar}</Ico>
    </InputLeftElement>
    <Input ref={ref} type="date" pl={8} {...inputProps} {...props} />
  </InputGroup>
));

/** Dropdown with icon + title + helper text per option (Billing Cycle / Payment Terms) */
function RichSelect({ value, options, onChange }: { value: number; options: Opt[]; onChange: (v: number) => void }) {
  const cur = options.find((o) => o.value === value);
  return (
    <Menu matchWidth isLazy>
      <MenuButton as={Button} size="sm" variant="outline" w="100%" textAlign="left" fontWeight="normal" borderRadius="md" rightIcon={<Ico>{I.chevron}</Ico>}>
        {cur?.label ?? String(value)}
      </MenuButton>
      <MenuList maxH="320px" overflowY="auto" zIndex={20}>
        {options.map((o) => (
          <MenuItem key={o.value} onClick={() => onChange(o.value)} bg={o.value === value ? "blue.50" : undefined}>
            <TagBox>{o.tag}</TagBox>
            <Box ml={3}>
              <Text fontSize="sm" fontWeight="medium" lineHeight="short">
                {o.label}
              </Text>
              {o.sub && (
                <Text fontSize="xs" color="gray.500">
                  {o.sub}
                </Text>
              )}
            </Box>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

/* ───────────────────────── component ───────────────────────── */

export default function ProjectBillingTab({ projectId, onSaved }: { projectId: number; onSaved?: () => void }) {
  const [summary, setSummary] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [listKey, setListKey] = useState(0);
  const isEdit = editingId !== null;
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [serviceBudgets, setServiceBudgets] = useState<any[]>([]);
  const [budgetsLoaded, setBudgetsLoaded] = useState(false);
  const [savedItems, setSavedItems] = useState<any[] | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [ready, setReady] = useState(false);
  const [fx, setFx] = useState<{ rate: number; rateDate: string; isStale?: boolean } | null>(null);
  const [clientCurrency, setClientCurrency] = useState("INR");

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useForm<Form>({ defaultValues: makeDefaults() });

  const type = Number(watch("billingType"));
  const frequency = Number(watch("billingFrequency"));
  const gst = Number(watch("gstPercent")) || 0;
  const terms = Number(watch("paymentTermsDays")) || 0;
  const autoGenerate = !!watch("autoGenerateInvoice");
  const isRecurring = type === 2;
  const currency = (watch("currencyCode") || "INR").toUpperCase();
  const money = (n: number) => formatMoney(n, currency);

  /* ---------- data ---------- */

  const loadServiceBudgets = async () => {
    try {
      const res = await fetchApi<any[]>(`ProjectServiceBudget/ProjectServiceBudget/${projectId}`, "GET");
      console.log("service budgets", res);
      setServiceBudgets(Array.isArray(res) ? res : []);
    } finally {
      setBudgetsLoaded(true);
    }
  };

  const load = async () => {
    try {
      const response = await fetchApi<any>(`projects/${projectId}/billing`, "GET", null, null, "");
      if (response) {
        setClient(response.client ?? response.project?.client ?? null);
        setSummary(response.summary);
        const cur = String(response.client?.currency || "INR").toUpperCase();
        setClientCurrency(cur);
        if (editingId === null) setValue("currencyCode", cur);
      }
    } finally {
      setLoading(false);
    }
  };

  const startEdit = async (billingId: number) => {
    const c = await fetchApi<any>(`projects/${projectId}/billing/${billingId}`, "GET", null, null, "");
    if (!c) return;
    const start = d(c.startDate) || today();
    const t = Number(c.paymentTermsDays ?? 30);
    reset({
      billingType: c.billingType,
      billingFrequency: c.billingFrequency || 3,
      startDate: start,
      nextInvoiceDate: d(c.nextInvoiceDate) || start,
      dueDate: d(c.dueDate) || addDays(start, t),
      endDate: d(c.endDate) || null,
      paymentTermsDays: t,
      gstPercent: Number(c.gstPercent ?? 18),
      autoGenerateInvoice: c.billingType === 2 ? !!c.autoGenerateInvoice : false,
      currencyCode: c.currencyCode ?? "INR",
      autoEmailClient: c.notificationChannels ? [1, 3, 7].includes(Number(c.notificationChannels)) : true,
    });
    setSavedItems(c.items ?? []);
    setEditingId(billingId);
    setReady(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetToCreate = () => {
     reset(makeDefaults(clientCurrency));
    setSavedItems(null);
    setEditingId(null);
    setReady(false);
  };

  const refreshHistory = () => window.dispatchEvent(new CustomEvent("history-refresh", { detail: { module: "ProjectBilling", recordId: projectId } }));

  useEffect(() => {
    load();
    loadServiceBudgets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (currency === "INR") {
      setFx(null);
      return;
    }
    let alive = true;
    fetchApi<any>(`exchange-rates/rate?from=${currency}&to=INR`, "GET", null, null, "").then((r) => {
      if (!alive) return;
      setFx(r ? { rate: Number(r.rate), rateDate: String(r.rateDate).substring(0, 10), isStale: !!r.isStale } : null);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  /* ---------- service rows ---------- */

  // project services of the wanted type (Recurring / One time), active only
  const budgetsFor = (t: number) => {
    const wanted = t === 2 ? ServiceType.Recurring : ServiceType.OneTime;
    return serviceBudgets.filter((b) => Number(b.serviceType ?? ServiceType.Recurring) === Number(wanted)).filter((b) => (b.status ?? "Active") === "Active");
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const budgetOptions = useMemo(() => budgetsFor(type), [serviceBudgets, type]);

  const buildPrefill = (t: number, f: number, g: number): Row[] => {
    const factor = t === 2 ? monthsFor(f) : 1;
    return budgetsFor(t).map((b) => {
      const base = amountOf(b);
      return {
        key: `svc-${b.id}`,
        serviceInterestedInId: Number(b.serviceInterestedInId),
        baseAmount: base,
        name: b.serviceName,
        description: b.serviceName,
        quantity: "1",
        unitPrice: String(r2(base * factor)),
        gstPercent: g,
      };
    });
  };

  // first fill of the table: saved items if there are any, otherwise the project's services
  useEffect(() => {
    if (ready || loading || !budgetsLoaded) return;
    const initType = Number(getValues("billingType"));
    const allowed = budgetsFor(initType);
    let saved = (savedItems ?? []).filter(Boolean);
    if (initType === 2) {
      saved = saved.filter((i: any) => i.serviceInterestedInId && allowed.some((b) => Number(b.serviceInterestedInId) === Number(i.serviceInterestedInId)));
    }
    if (saved.length) {
      const g = Number(getValues("gstPercent")) || 0;
      setRows(
        saved.map((i: any, idx: number) => {
          const b = serviceBudgets.find((x) => Number(x.serviceInterestedInId) === Number(i.serviceInterestedInId));
          return {
            key: `s-${idx}`,
            serviceInterestedInId: i.serviceInterestedInId ? Number(i.serviceInterestedInId) : undefined,
            custom: !i.serviceInterestedInId,
            baseAmount: b ? amountOf(b) : undefined,
            name: i.name ?? "",
            description: i.description ?? i.name ?? "",
            quantity: String(i.quantity ?? 1),
            unitPrice: String(Number(i.unitPrice) > 0 ? i.unitPrice : b ? r2(amountOf(b) * (initType === 2 ? monthsFor(Number(getValues("billingFrequency"))) : 1)) : (i.amount ?? "")),
            gstPercent: Number(i.gstPercent ?? g),
          };
        }),
      );
    } else {
      setRows(buildPrefill(Number(getValues("billingType")), Number(getValues("billingFrequency")), Number(getValues("gstPercent")) || 0));
    }
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, budgetsLoaded, savedItems, ready]);

  const patchRow = (key: string, p: Partial<Row>) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const removeRow = (key: string) => setRows((prev) => prev.filter((r) => r.key !== key));
  const addRow = () => setRows((prev) => [...prev, { key: `n-${Date.now()}`, name: "", description: "", quantity: "1", unitPrice: "", gstPercent: gst }]);

  const pickService = (row: Row, val: string) => {
    if (val === "") return patchRow(row.key, { serviceInterestedInId: undefined, custom: false, baseAmount: undefined, name: "" });
    if (val === "custom") return patchRow(row.key, { serviceInterestedInId: undefined, custom: true, baseAmount: undefined, name: "" });
    const b = budgetOptions.find((x) => Number(x.serviceInterestedInId) === Number(val));
    if (!b) return;
    const base = amountOf(b);
    const factor = isRecurring ? monthsFor(frequency) : 1;
    patchRow(row.key, {
      serviceInterestedInId: Number(val),
      custom: false,
      baseAmount: base,
      name: b.serviceName,
      description: !row.description.trim() || row.description === row.name ? b.serviceName : row.description,
      unitPrice: String(r2(base * factor)),
    });
  };

  const totals = useMemo(() => {
    let sub = 0;
    let tax = 0;
    rows.forEach((r) => {
      const b = lineBase(r);
      sub += b;
      tax += (b * (Number(r.gstPercent) || 0)) / 100;
    });
    return { sub: r2(sub), tax: r2(tax), total: r2(sub + tax) };
  }, [rows]);

  /* ---------- field handlers ---------- */

  const onTypeChange = (t: number) => {
    setValue("billingType", t);
    setValue("autoGenerateInvoice", t === 2 ? true : !isEdit);
    setRows(buildPrefill(t, frequency, gst));
  };
  const onFrequencyChange = (f: number) => {
    setValue("billingFrequency", f);
    setRows((prev) => prev.map((r) => (r.baseAmount != null && !r.custom ? { ...r, unitPrice: String(r2(r.baseAmount * monthsFor(f))) } : r)));
  };
  const onStartChange = (e: any) => {
    const s = e.target.value;
    setValue("dueDate", addDays(s, Number(getValues("paymentTermsDays")) || 0));
    const n = getValues("nextInvoiceDate");
    if (!n || n < s) setValue("nextInvoiceDate", s);
  };
  const onTermsChange = (v: number) => {
    setValue("paymentTermsDays", v);
    setValue("dueDate", addDays(getValues("startDate"), v));
  };

  const termOptions = TERMS.some((t) => t.value === terms) ? TERMS : [...TERMS, { value: terms, label: `${terms} Days`, tag: String(terms) }].sort((a, b) => a.value - b.value);

  /* ---------- actions ---------- */

  const generateInvoice = async (billingId: number) => {
    const r = await fetchApi<any>(`projects/${projectId}/billing/${billingId}/generate`, "POST", null, null, "");
    if (!r) {
      addToast({ status: "error", message: "Invoice already exists for this billing period." });
      return false;
    }
    addToast({ status: "success", message: `Created ${r.invoiceNumber}` });
    return true;
  };

  const onSubmit = async (v: Form, generateNow = false) => {
    const recurring = Number(v.billingType) === 2;
    if (!v.startDate) {
      addToast({ status: "error", message: recurring ? "Start Date is required." : "Invoice Date is required." });
      return;
    }
    if (recurring) {
      if (!v.nextInvoiceDate) {
        addToast({ status: "error", message: "Next Invoice Date is required." });
        return;
      }
      if (v.nextInvoiceDate < v.startDate) {
        addToast({ status: "error", message: "Next Invoice Date cannot be before Start Date." });
        return;
      }
    } else {
      if (!v.dueDate) {
        addToast({ status: "error", message: "Due Date is required." });
        return;
      }
      if (v.dueDate < v.startDate) {
        addToast({ status: "error", message: "Due Date cannot be before Invoice Date." });
        return;
      }
    }
    const items = rows
      .filter((r) => !recurring || (r.serviceInterestedInId && budgetOptions.some((b) => Number(b.serviceInterestedInId) === r.serviceInterestedInId)))
      .filter((r) => (Number(r.quantity) || 0) > 0 && (Number(r.unitPrice) || 0) > 0 && (r.serviceInterestedInId || r.description.trim()))
      .map((r) => {
        const amount = r2(lineBase(r));
        const gstAmount = r2((amount * (Number(r.gstPercent) || 0)) / 100);
        return {
          serviceInterestedInId: r.serviceInterestedInId ?? null,
          name: (r.name || r.description).trim(),
          description: r.description.trim(),
          quantity: Number(r.quantity),
          unitPrice: Number(r.unitPrice),
          gstPercent: Number(r.gstPercent) || 0,
          amount, // before GST
          gstAmount,
          totalAmount: r2(amount + gstAmount),
          isCustom: !r.serviceInterestedInId,
        };
      });

    if (!items.length) {
      addToast({ status: "error", message: "Add at least one service with quantity and unit price." });
      return;
    }

    const res = await fetchApi<any>(
      isEdit ? `projects/${projectId}/billing/${editingId}` : `projects/${projectId}/billing`,
      isEdit ? "PUT" : "POST",
      {
        billingType: +v.billingType,
        billingFrequency: recurring ? +v.billingFrequency : 0,
        subtotal: totals.sub,
        gstAmount: totals.tax,
        invoiceAmount: totals.total,
        startDate: v.startDate,
        nextInvoiceDate: recurring ? v.nextInvoiceDate || v.startDate : null,
        dueDate: recurring ? null : v.dueDate || null,
        endDate: recurring ? v.endDate || null : null,
        paymentTermsDays: +v.paymentTermsDays,
        gstPercent: +v.gstPercent,
        // one-time: the invoice is generated explicitly below, so don't also ask the backend to auto-generate
        autoGenerateInvoice: recurring ? v.autoGenerateInvoice : false,
        autoEmailClient: v.autoEmailClient,
        notificationChannels: v.autoEmailClient ? 3 : 2, // 3 = Email + In-App, 2 = In-App
        items,
        currencyCode: v.currencyCode,
      },
      null,
      "",
    );
    if (!res) return;
    addToast({ status: "success", message: isEdit ? "Billing plan updated" : "Billing plan created" });
    if ((!recurring && v.autoGenerateInvoice) || (recurring && generateNow)) await generateInvoice(res.id);
    resetToCreate();
    load();
    setListKey((k) => k + 1);
    refreshHistory();
  };

  const onInvalid = (errors: any) => {
    if (errors.startDate) addToast({ status: "error", message: isRecurring ? "Start Date is required." : "Invoice Date is required." });
    else if (errors.nextInvoiceDate) addToast({ status: "error", message: "Next Invoice Date is required." });
    else if (errors.dueDate) addToast({ status: "error", message: "Due Date is required." });
  };

  const onCancel = () => resetToCreate();

  if (loading) return <Spinner />;

  /* ---------- shared pieces ---------- */

  // const ci = {
  //   name: client?.name ?? client?.clientName ?? client?.companyName ?? "—",
  //   gstin: client?.gstin ?? client?.gstNumber,
  //   state: client?.state,
  //   address: client?.billingAddress ?? client?.address,
  // };

  // const clientSelect = (
  //   <FormControl>
  //     <FormLabel {...labelProps}>Client{Req}</FormLabel>
  //     <Select {...inputProps} isDisabled value={ci.name} onChange={() => {}} icon={<Ico>{I.chevron}</Ico>}>
  //       <option value={ci.name}>{ci.name}</option>
  //     </Select>
  //   </FormControl>
  // );

  // const clientInfo = (
  //   <Flex mt={3} gap={2} bg="blue.50" borderRadius="md" p={3} fontSize="sm" color="gray.700">
  //     <Ico color="blue.500" mt="3px" flexShrink={0}>
  //       {I.info}
  //     </Ico>
  //     <Box>
  //       <Text fontWeight="semibold">{ci.name}</Text>
  //       {ci.gstin && <Text>GSTIN: {ci.gstin}</Text>}
  //       {ci.state && <Text>State: {ci.state}</Text>}
  //       {ci.address && <Text>Billing Address: {ci.address}</Text>}
  //     </Box>
  //   </Flex>
  // );

  const typeRadio = (
    <FormControl>
      <FormLabel {...labelProps}>Billing Type{Req}</FormLabel>
      <RadioGroup value={String(type)} onChange={(v) => onTypeChange(Number(v))}>
        <HStack spacing={6} h="32px">
          <Radio value="1" colorScheme="blue" size="sm">
            One-Time
          </Radio>
          <Radio value="2" colorScheme="blue" size="sm">
            Recurring
          </Radio>
        </HStack>
      </RadioGroup>
    </FormControl>
  );
  const currencyField = (
    <FormControl>
      <FormLabel {...labelProps}>Billing Currency{Req}</FormLabel>
      <Select {...inputProps} {...register("currencyCode")}>
        {CURRENCIES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.label}
          </option>
        ))}
      </Select>
    </FormControl>
  );

  const startDateField = (label: string) => (
    <FormControl>
      <FormLabel {...labelProps}>
        {label}
        {Req}
      </FormLabel>
      <DateInput {...register("startDate", { required: true, onChange: onStartChange })} />
    </FormControl>
  );

  const termsSelect = (
    <Controller
      control={control}
      name="paymentTermsDays"
      render={({ field }) => (
        <RichSelect
          value={Number(field.value)}
          options={termOptions}
          onChange={(v) => {
            field.onChange(v);
            onTermsChange(v);
          }}
        />
      )}
    />
  );

  const summaryLines = (
    <VStack align="stretch" spacing={2} fontSize="sm">
      <Flex justify="space-between">
        <Text>Subtotal</Text>
        <Text fontWeight="semibold">{money(totals.sub)}</Text>
      </Flex>
      <Flex justify="space-between">
        <Text>Total GST</Text>
        <Text fontWeight="semibold">{money(totals.tax)}</Text>
      </Flex>
      <Flex justify="space-between" align="center" borderTop="1px solid" borderColor="blue.100" pt={2}>
        <Text fontWeight="bold" color={NAVY} fontSize="md">
          {isRecurring ? "Total Amount" : "Total Amount"}
        </Text>
        <Text fontWeight="bold" color="blue.600" fontSize="xl">
          {money(totals.total)}
        </Text>
      </Flex>
      {currency !== "INR" && fx && (
        <Text fontSize="xs" color="gray.500" textAlign="right">
          ≈ {formatMoney(totals.total * fx.rate, "INR")} · 1 {currency} = ₹{fx.rate} ({fx.rateDate}). Final rate is fixed when the invoice is generated.
        </Text>
      )}
      {currency !== "INR" && fx?.isStale && (
        <Text fontSize="xs" color="orange.500" textAlign="right">
          This rate is not from today. It may change when the invoice is generated.
        </Text>
      )}
    </VStack>
  );

  const servicesCard = (
    <Box {...cardProps} p={4}>
      <Flex justify="space-between" align="flex-start" gap={3} mb={3}>
        <SectionTitle icon={I.layers} title={isRecurring ? "Billing Services" : "Services"} sub={isRecurring ? "Add multiple services to this billing plan" : "Add multiple services to this billing plan. Tax % will be auto-filled from settings and can be changed."} />
        <Button size="sm" variant="outline" colorScheme="blue" flexShrink={0} leftIcon={<Ico>{I.plus}</Ico>} onClick={addRow}>
          Add Service
        </Button>
      </Flex>

      <Box overflowX="auto" border="1px solid" borderColor="gray.100" borderRadius="md">
        <Table size="sm" variant="simple" sx={{ th: { textTransform: "none", letterSpacing: "normal", fontSize: "xs", color: "gray.600", bg: "gray.50" }, td: { verticalAlign: "middle" } }}>
          <Thead>
            <Tr>
              <Th w="36px">#</Th>
              <Th minW="170px">Service{Req}</Th>
              <Th minW="190px">Description</Th>
              <Th w="80px">Qty{Req}</Th>
              <Th w="130px">
                Unit Price ({currency}){Req}
              </Th>
              <Th w="100px">GST %{Req}</Th>
              <Th isNumeric w="110px">
                {isRecurring ? "Total" : "Amount"} ({currency})
              </Th>
              <Th w="70px" textAlign="center">
                Action
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r, idx) => {
              const selected = r.serviceInterestedInId ? String(r.serviceInterestedInId) : r.custom ? "custom" : "";
              const inList = !r.serviceInterestedInId || budgetOptions.some((b) => Number(b.serviceInterestedInId) === r.serviceInterestedInId);
              return (
                <Tr key={r.key}>
                  <Td>{idx + 1}</Td>
                  <Td>
                    <Select {...inputProps} value={selected} onChange={(e) => pickService(r, e.target.value)}>
                      <option value="">Select service</option>
                      {budgetOptions.map((b) => (
                        <option key={b.id} value={b.serviceInterestedInId}>
                          {b.serviceName}
                        </option>
                      ))}
                      {!inList && !isRecurring && <option value={r.serviceInterestedInId}>{r.name}</option>}
                      {!isRecurring && <option value="custom">Custom service</option>}
                    </Select>
                  </Td>
                  <Td>
                    <Textarea {...inputProps} rows={2} resize="none" value={r.description} onChange={(e) => patchRow(r.key, { description: e.target.value })} />
                  </Td>
                  <Td>
                    <Input {...inputProps} type="number" min={1} textAlign="center" value={r.quantity} onChange={(e) => patchRow(r.key, { quantity: e.target.value })} />
                  </Td>
                  <Td>
                    <Input {...inputProps} type="number" min={0} step="0.01" value={r.unitPrice} onChange={(e) => patchRow(r.key, { unitPrice: e.target.value })} />
                  </Td>
                  <Td>
                    <Select {...inputProps} value={r.gstPercent} onChange={(e) => patchRow(r.key, { gstPercent: Number(e.target.value) })}>
                      {(GST_OPTIONS.includes(r.gstPercent) ? GST_OPTIONS : [...GST_OPTIONS, r.gstPercent]).map((g) => (
                        <option key={g} value={g}>
                          {g}%
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td isNumeric fontWeight="medium">
                     {money(r2(lineTotal(r)))}
                  </Td>
                  <Td textAlign="center">
                    <Button size="sm" bg="red.50" color="red.500" _hover={{ bg: "red.100" }} aria-label="Remove service" onClick={() => removeRow(r.key)}>
                      <Ico>{I.trash}</Ico>
                    </Button>
                  </Td>
                </Tr>
              );
            })}
            {rows.length === 0 && (
              <Tr>
                <Td colSpan={8}>
                  <Text fontSize="sm" color="gray.500" py={2} textAlign="center">
                    No services added. Use "Add Service".
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {isRecurring && (
        <Flex mt={3} justify="space-between" align="flex-start" gap={4} wrap="wrap">
          <Flex gap={2} fontSize="xs" color="gray.500" align="center">
            <Ico>{I.info}</Ico>
            <Text>Default tax from settings; editable per service</Text>
          </Flex>
          <Box w={{ base: "100%", md: "280px" }} bg="gray.50" borderRadius="md" p={3}>
            {summaryLines}
          </Box>
        </Flex>
      )}
    </Box>
  );

  const gstBlock = (boxed: boolean) => (
    <Box {...(boxed ? { ...cardProps, p: 4 } : {})}>
      <Flex align="flex-start" gap={3}>
        {boxed ? null : <IconBox>{I.percent}</IconBox>}
        <Box flex={1}>
          {boxed ? (
            <SectionTitle icon={I.percent} title="GST / Tax" />
          ) : (
            <Text fontWeight="semibold" fontSize="sm" mb={2} color={NAVY}>
              GST / Tax
            </Text>
          )}
          {boxed && (
            <Text fontSize="xs" color="gray.600" mt={3} mb={1}>
              Tax % (applies to all services)
            </Text>
          )}
          <Select
            {...inputProps}
            {...register("gstPercent", {
              valueAsNumber: true,
              onChange: (e) => {
                const g = Number(e.target.value) || 0;
                setRows((prev) => prev.map((r) => ({ ...r, gstPercent: g })));
              },
            })}
          >
            {GST_OPTIONS.map((g) => (
              <option key={g} value={g}>
                {g}%{g ? " (IGST/CGST+SGST as applicable)" : " (No tax)"}
              </option>
            ))}
          </Select>
          <Flex mt={2} gap={2} fontSize="xs" color="gray.500" align="center">
            <Ico>{I.info}</Ico>
            <Text>Selecting a tax rate here applies it to all service lines. You can still change the rate for an individual service above.</Text>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );

  const toggle = (name: "autoGenerateInvoice" | "autoEmailClient", title: string, sub: string, icon?: ReactNode) => (
    <Flex align="center" gap={3}>
      {icon && <IconBox>{icon}</IconBox>}
      <Controller control={control} name={name} render={({ field }) => <Switch colorScheme="blue" isChecked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} />
      <Box>
        <Text fontSize="sm" fontWeight="semibold" lineHeight="short">
          {title}
        </Text>
        <Text fontSize="xs" color="gray.500">
          {sub}
        </Text>
      </Box>
    </Flex>
  );

  const submitLabel = `${isEdit ? "Update Plan" : "Create Plan"}${!isRecurring && autoGenerate ? " & Generate Invoice" : ""}`;
  const footer = (
    <HStack mt={5} justify="flex-end" spacing={3}>
      <Button size="sm" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      {isRecurring && (
        <Button size="sm" variant="outline" colorScheme="blue" isLoading={isSubmitting} onClick={handleSubmit((v) => onSubmit(v, true), onInvalid)}>
          {isEdit ? "Update Plan" : "Create Plan"} & Generate Invoice Now
        </Button>
      )}
      <Button size="sm" type="submit" colorScheme="blue" px={6} isLoading={isSubmitting}>
        {submitLabel}
      </Button>
    </HStack>
  );
  /* ---------- layouts ---------- */

  const oneTimeLayout = (
    <VStack align="stretch" spacing={4}>
      <Box {...cardProps} p={4}>
        <SectionTitle icon={I.doc} title="Billing Details" />
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mt={3} alignItems="start">
          {typeRadio}
          {currencyField}
          {startDateField("Invoice Date")}
          <FormControl>
            <FormLabel {...labelProps}>Due Date{Req}</FormLabel>
            <DateInput
              {...register("dueDate", {
                required: true,
                onChange: (e) => {
                  const val = e.target.value;
                  if (val && val < getValues("startDate")) {
                    addToast({ status: "error", message: "Due Date cannot be before Start Date." });
                  }
                },
              })}
            />
          </FormControl>
          <FormControl>
            <FormLabel {...labelProps}>Payment Terms{Req}</FormLabel>
            {termsSelect}
            <Text fontSize="xs" color="gray.500" mt={1}>
              Client is expected to make payment within {terms} days from the invoice date.
            </Text>
          </FormControl>
        </SimpleGrid>
      </Box>

      {servicesCard}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        {gstBlock(true)}
        <Box {...cardProps} p={4} bg="blue.50">
          <SectionTitle icon={I.calc} title="Invoice Summary" />
          <Box mt={3}>{summaryLines}</Box>
        </Box>
      </SimpleGrid>

      <Box {...cardProps} p={4}>
        <SectionTitle icon={I.sliders} title="Additional Options" />
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={3}>
          {toggle("autoGenerateInvoice", "Generate Invoice Now", "Create invoice immediately after saving this plan.")}
          {/* {toggle("autoEmailClient", "Email Invoice to Client", "Send invoice automatically to client's billing email.")} */}
        </SimpleGrid>
      </Box>
    </VStack>
  );

  const recurringLayout = (
    <VStack align="stretch" spacing={4}>
      <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4} alignItems="start">
        {typeRadio}
        {currencyField}
        <FormControl>
          <FormLabel {...labelProps}>Billing Cycle{Req}</FormLabel>
          <RichSelect value={frequency} options={CYCLES} onChange={onFrequencyChange} />
        </FormControl>

        {startDateField("Start Date")}

        <FormControl>
          <FormLabel {...labelProps}>Next Invoice Date{Req}</FormLabel>
          <DateInput {...register("nextInvoiceDate", { required: isRecurring })} />
        </FormControl>

        <FormControl>
          <FormLabel {...labelProps}>Payment Terms{Req}</FormLabel>
          {termsSelect}
        </FormControl>
      </SimpleGrid>

      {servicesCard}
      {gstBlock(false)}

      {toggle("autoGenerateInvoice", "Auto-generate invoice", "System will create invoices automatically on each Next Invoice Date. Use 'Generate Invoice Now' below to create the first invoice immediately.", I.doc)}
    </VStack>
  );
  return (
    <Box>
      <Box {...cardProps} p={5}>
        <Heading size="md" color={NAVY}>
          {isEdit ? "Update" : "Create"} Billing Plan{isRecurring ? "" : " – One Time"}
        </Heading>
        <Text fontSize="sm" color="gray.500" mb={4}>
          {isRecurring ? "Configure services, billing cycle and automate invoice generation" : "Add services and create a one-time invoice for your client."}
        </Text>
        <form onSubmit={handleSubmit((v) => onSubmit(v, false), onInvalid)}>
          {isRecurring ? recurringLayout : oneTimeLayout}
          {footer}
        </form>
      </Box>
      {/* Billing Plans List */}
      <Box mt={6}>
        <BillingPlanList projectId={projectId} editingId={editingId} reloadKey={listKey} onEdit={startEdit} />
      </Box>

      {/* Summary */}
      {summary && (
        <Box mt={6}>
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="semibold" fontSize="sm">
              Project Billing Summary
            </Text>
            {editingId && (
              <Button
                size="xs"
                variant="outline"
                onClick={async () => {
                  if (await generateInvoice(editingId!)) {
                    load();
                    setListKey((k) => k + 1);
                  }
                }}
              >
                Generate invoice now
              </Button>
            )}
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4}>
            <StatCard label="Total invoices" value={String(summary.totalInvoices)} icon={ICONS.file} tone="gray" />
            <StatCard label="Paid" value={inr(summary.paid)} icon={ICONS.check} tone="green" valueColor="green.600" />
            <StatCard label="Outstanding" value={inr(summary.outstanding)} icon={ICONS.alert} tone="orange" valueColor="orange.500" />
            <StatCard label="Next invoice date" value={showDateTime(summary.nextInvoiceDate)} icon={ICONS.calendar} tone="blue" small />
            {summary?.latestInvoice ? <InvoicePdfButtons projectId={projectId} invoiceId={summary.latestInvoice.id} invoiceNumber={summary.latestInvoice.invoiceNumber} /> : <StatCard label="Latest invoice" value="Not generated yet" icon={ICONS.file} tone="gray" small />}
          </SimpleGrid>
        </Box>
      )}

      {/* Activity history
      <Box {...cardProps} p={5} mt={6}>
        <Heading size="sm" mb={4}>Billing Activity</Heading>
        <UniversalActivityHistory module="ProjectBilling" recordId={projectId} maxH="400px" />
      </Box> */}
    </Box>
  );
}
